import React, { useState, useEffect } from 'react';
import { Lock, AlertCircle, ShoppingBag, Sparkles, Clock, Mail, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';
import MarketingStudio from './MarketingStudio';
import CreditsBanner from '../shared/CreditsBanner';

const ProtectedMarketingStudio = ({ onBack }) => {
  // ✅ MODÈLE 100% CRÉDITS : plus aucune vérification d'abonnement.
  // Le studio est toujours accessible ; la génération est limitée par les crédits.
  const [loading] = useState(false);
  const [hasAccess] = useState(true);
  const [accessData] = useState(null);
  const [error] = useState(null);

  // État de chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Clock className="w-16 h-16 text-purple-500 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Vérification de votre accès...
          </h2>
          <p className="text-gray-600">Patientez un instant</p>
        </div>
      </div>
    );
  }

  // Erreur technique
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Erreur
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour au Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ✅ MODÈLE 100% CRÉDITS (juillet 2026) : plus de blocage par abonnement.
  // Le studio est toujours accessible — la bannière affiche les crédits restants
  // et la génération est limitée par les crédits achetés.
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <CreditsBanner serviceId="marketing_studio" />
      </div>
      <MarketingStudio onBack={onBack} />
    </div>
  );

  // --- Ancien blocage par abonnement (conservé pour référence, jamais atteint) ---
  // eslint-disable-next-line no-unreachable
  if (!hasAccess) {
    const reason = accessData?.reason || 'unknown';
    const message = accessData?.message || 'Accès non autorisé';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 text-white text-center">
            <Lock className="w-20 h-20 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-2">
              Accès Restreint
            </h1>
            <p className="text-purple-100">
              Marketing Studio TeckiA
            </p>
          </div>
          
          {/* Content */}
          <div className="p-8">
            
            {/* Message selon raison */}
            {reason === 'no_subscription' && (
              <>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mb-6">
                  <div className="flex items-start">
                    <ShoppingBag className="w-6 h-6 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-yellow-800 mb-1">
                        Aucun abonnement actif
                      </h3>
                      <p className="text-sm text-yellow-700">
                        Vous n'avez pas encore d'abonnement Marketing Studio.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 mb-8">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                    Fonctionnalités Marketing Studio
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
                      <h4 className="font-bold text-purple-900 mb-2">
                        🎨 Affiches Marketing Pro
                      </h4>
                      <p className="text-sm text-purple-700">
                        Créez des affiches publicitaires avec TeckiA Image
                      </p>
                    </div>

                    <div className="bg-pink-50 rounded-xl p-4 border-2 border-pink-200">
                      <h4 className="font-bold text-pink-900 mb-2">
                        🎬 Vidéos TeckiA
                      </h4>
                      <p className="text-sm text-pink-700">
                        Transformez vos affiches en vidéos animées 6s
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                      <h4 className="font-bold text-blue-900 mb-2">
                        👤 Modèles Personnalisés
                      </h4>
                      <p className="text-sm text-blue-700">
                        Utilisez vos propres modèles ou IA générique
                      </p>
                    </div>
                    
                    <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200">
                      <h4 className="font-bold text-green-900 mb-2">
                        💾 Stockage Illimité
                      </h4>
                      <p className="text-sm text-green-700">
                        Sauvegardez toutes vos créations
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {reason === 'subscription_inactive' && (
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-6 h-6 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-orange-800 mb-1">
                      Abonnement Inactif
                    </h3>
                    <p className="text-sm text-orange-700">
                      {message}
                    </p>
                    {accessData?.subscription && (
                      <div className="mt-2 text-xs text-orange-600">
                        <p><strong>Statut :</strong> {accessData.subscription.status}</p>
                        <p><strong>Service :</strong> {accessData.subscription.service_name}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {reason === 'subscription_not_found' && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-red-800 mb-1">
                      Erreur Abonnement
                    </h3>
                    <p className="text-sm text-red-700">
                      Votre abonnement n'a pas pu être trouvé. Contactez l'administrateur.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="space-y-3">
              <a
                href="mailto:contact@tdidigital.io?subject=Demande d'abonnement Marketing Studio"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-bold"
              >
                <Mail className="w-5 h-5" />
                Contacter l'Administrateur
              </a>
              
              <button
                onClick={onBack}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                <ArrowLeft className="w-5 h-5" />
                Retour au Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ ACCÈS AUTORISÉ
  return <MarketingStudio onBack={onBack} />;
};

export default ProtectedMarketingStudio;